import { api, mintViewerToken } from '../../api';

// Fetch raw document bytes for client-side rendering.
//
// Architecture: when GCS signed URLs are available (production), the
// browser pulls bytes directly from storage.googleapis.com — bypassing
// the Cloud Run API container entirely. This is much faster than the
// /view endpoint, which downloads from GCS into the container's memory
// and re-streams it (a needless round-trip that pins instance time).
//
// Falls back to /view when /media-url returns api_fallback (local dev,
// no GCS configured).
export async function fetchFileBytes(documentId, _fileType, setStep = () => {}) {
  setStep('resolving signed URL');
  let mediaInfo;
  try {
    mediaInfo = await api.get(`/documents/${documentId}/media-url`);
  } catch (err) {
    // If /media-url fails, fall through to /view rather than aborting.
    mediaInfo = null;
  }

  const useSigned = mediaInfo?.url && mediaInfo?.source === 'gcs_signed';
  const url = useSigned ? mediaInfo.url : null;

  setStep(useSigned ? 'downloading from GCS' : 'downloading via API');

  let res;
  if (useSigned) {
    // Signed URL — direct fetch, no auth header needed (the URL itself
    // is the auth). storage.googleapis.com sets CORS allow-origin: *.
    res = await fetch(url);
  } else {
    // Local-dev fallback or media-url unavailable.
    res = await api.raw('GET', `/documents/${documentId}/view`);
  }

  if (!res.ok) throw new Error(`HTTP ${res.status} fetching document bytes`);
  setStep('reading body');
  return await res.arrayBuffer();
}

// Fetch a usable URL pointing at the document for renderers that want a
// URL rather than ArrayBuffer (e.g. <img>). Returns `{url, source}` so
// the caller can emit media-fetch telemetry that distinguishes signed-GCS
// from viewer-token-fallback failures.
//
// source values:
//   'gcs_signed'        — production happy path, browser fetches GCS directly
//   'viewer_token'      — /view fallback with a minted viewer token
//   'viewer_token_unauth' — /view fallback without a token (will 401, kept for
//                           surface visibility; renderer should still attempt)
export async function fetchFileUrl(documentId) {
  try {
    const m = await api.get(`/documents/${documentId}/media-url`);
    if (m?.url && m?.source === 'gcs_signed') {
      return { url: m.url, source: 'gcs_signed' };
    }
  } catch {
    // fall through
  }
  // Local-dev / no-GCS fallback. Mint a viewer token so the <img> request
  // doesn't 401 (we never put a Firebase ID token in URLs).
  try {
    const vt = await mintViewerToken(documentId);
    return {
      url: `/api/v1/documents/${documentId}/view?vt=${encodeURIComponent(vt)}`,
      source: 'viewer_token',
    };
  } catch {
    // Final fallback — request will 401, surfaced by the renderer.
    return {
      url: `/api/v1/documents/${documentId}/view`,
      source: 'viewer_token_unauth',
    };
  }
}
